import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,  // v6 F9.8 — required so session_token cookie flows to backend
});

// Clients
export const listClients = () => api.get("/clients").then((r) => r.data);
export const createClient = (data) => api.post("/clients", data).then((r) => r.data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data).then((r) => r.data);
export const deleteClient = (id) => api.delete(`/clients/${id}`).then((r) => r.data);
export const getClient = (id) => api.get(`/clients/${id}`).then((r) => r.data);

// Projects
export const listProjects = (clientId) =>
  api.get("/projects", { params: clientId ? { client_id: clientId } : {} }).then((r) => r.data);
export const createProject = (data) => api.post("/projects", data).then((r) => r.data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data).then((r) => r.data);
export const deleteProject = (id) => api.delete(`/projects/${id}`).then((r) => r.data);
export const getProject = (id) => api.get(`/projects/${id}`).then((r) => r.data);

// Quotes
export const listQuotes = (projectId) =>
  api.get("/quotes", { params: projectId ? { project_id: projectId } : {} }).then((r) => r.data);
export const createQuote = (data) => api.post("/quotes", data).then((r) => r.data);
export const updateQuote = (id, data) => api.put(`/quotes/${id}`, data).then((r) => r.data);
export const deleteQuote = (id) => api.delete(`/quotes/${id}`).then((r) => r.data);
export const getQuote = (id) => api.get(`/quotes/${id}`).then((r) => r.data);
export const calculateQuote = (data) => api.post("/quotes/calculate", data).then((r) => r.data);
export const quotePdfUrl = (id) => `${API}/quotes/${id}/pdf`;
export const renumberQuote = (id) => api.post(`/quotes/${id}/renumber`).then((r) => r.data);

// v14z — Authenticated PDF-open flow.
// Vervangt <a target="_blank" href=…/pdf> die soms {"detail":"Not found"} gaf
// wanneer de browser meerdere sessies naast elkaar had (stale cookie → andere
// owner → 404 op _get_owned_or_404). Deze helper haalt de PDF via de gedeelde
// axios-client (withCredentials + eventuele Bearer-tokens), zodat de PDF exact
// dezelfde sessie-context gebruikt als de rest van de app.
//
// Popup-blocker gotcha: window.open() moet SYNCHROON in de user-gesture-handler
// gebeuren. Daarom openen we eerst een lege tab en zetten pas na de fetch de
// blob-URL erop. Als de popup-blocker het toch tegenhoudt, valt de blob-URL
// terug op window.location van de huidige tab.
export const openPdfBlob = async (path) => {
  const win = window.open("about:blank", "_blank");
  try {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    if (win && !win.closed) {
      win.location = blobUrl;
      try { win.focus(); } catch { /* focus is best-effort */ }
    } else {
      window.location.href = blobUrl;
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return true;
  } catch (e) {
    if (win && !win.closed) win.close();
    throw e;
  }
};
export const openInvoicePdf = (id) => openPdfBlob(`/invoices/${id}/pdf`);
export const openQuotePdf = (id) => openPdfBlob(`/quotes/${id}/pdf`);

// v14at-p6 — Native browser-download zonder Blob-URL tussenstap.
// Lost het Chrome "Mislukt — Netwerkfout" probleem op dat optrad wanneer
// de PDF-viewer secundair de blob-URL probeerde op te halen nadat die
// (soms) al door `URL.revokeObjectURL` was ingetrokken, of wanneer Chrome's
// download-manager de blob-eigenaar niet correct kon resolven.
//
// Deze helper gebruikt het backend `?download=1` endpoint dat met
// `Content-Disposition: attachment` reageert; de browser downloadt
// rechtstreeks naar de Downloads-folder met de door backend gezette
// filename (geen UUID exposure, zie `_invoice_pdf_filename`). Auth gaat
// via dezelfde session_token cookie als axios (withCredentials).
export const downloadPdfDirect = (path) => {
  const url = `${API}${path}${path.includes("?") ? "&" : "?"}download=1`;
  const a = document.createElement("a");
  a.href = url;
  // `download` attribute is advisory; de echte filename komt van
  // Content-Disposition header. We laten geen value achter om de backend
  // de source of truth te houden (consistent met e-mail bijlagen).
  a.rel = "noopener";
  // Hidden & auto-remove; same-origin dus `download` wordt gehonoreerd.
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
export const downloadInvoicePdf = (id) => downloadPdfDirect(`/invoices/${id}/pdf`);
export const downloadQuotePdf = (id) => downloadPdfDirect(`/quotes/${id}/pdf`);
export const setProjectActiveTemplate = (pid, payload) =>
  api.put(`/projects/${pid}/active-template`, payload).then((r) => r.data);
export const quotesFeed = (params = {}) => api.get("/quotes-feed", { params }).then((r) => r.data);
export const setProjectQuoteStatus = (pid, quote_status) =>
  api.put(`/projects/${pid}/status`, { quote_status }).then((r) => r.data);

// Price codes
export const listPriceCodes = (params = {}) =>
  api.get("/price-codes", { params }).then((r) => r.data);
export const createPriceCode = (data) => api.post("/price-codes", data).then((r) => r.data);
export const updatePriceCode = (id, data) => api.put(`/price-codes/${id}`, data).then((r) => r.data);
export const deletePriceCode = (id) => api.delete(`/price-codes/${id}`).then((r) => r.data);

// Company
export const getCompany = () => api.get("/company").then((r) => r.data);
export const updateCompany = (data) => api.put("/company", data).then((r) => r.data);

// Templates
export const listTemplates = (opts = {}) =>
  api.get("/templates", { params: opts }).then((r) => r.data);
export const createTemplate = (data) => api.post("/templates", data).then((r) => r.data);
export const updateTemplate = (id, data) => api.put(`/templates/${id}`, data).then((r) => r.data);
export const deleteTemplate = (id) => api.delete(`/templates/${id}`).then((r) => r.data);
export const duplicateTemplate = (id) => api.post(`/templates/${id}/duplicate`).then((r) => r.data);
export const getTemplate = (id) => api.get(`/templates/${id}`).then((r) => r.data);
export const applyTemplateToProject = (projectId, templateId) =>
  api.post(`/projects/${projectId}/apply-template/${templateId}`).then((r) => r.data);
export const applyTemplateLinesToProject = (projectId, templateId, payload) =>
  api.post(`/projects/${projectId}/apply-template-lines/${templateId}`, payload).then((r) => r.data);

// v6 F16 S1 — Auto-source catalog (voor sjabloon-editor bron-selector)
export const listAutoSources = () => api.get("/auto-sources").then((r) => r.data);

// v6 F19 — Beta waitlist (admin lijst + status wijziging)
export const listBetaWaitlist = () => api.get("/beta-waitlist").then((r) => r.data);
export const updateBetaWaitlistStatus = (id, status) =>
  api.patch(`/beta-waitlist/${id}`, { status }).then((r) => r.data);

// v6 Fase C — photo upload per work_zone
export const uploadZonePhoto = (quoteId, zoneId, dataUrl, label = "before") =>
  api.post(`/quotes/${quoteId}/zones/${zoneId}/photos`, { data_url: dataUrl, label }).then((r) => r.data);
export const deleteZonePhoto = (quoteId, zoneId, idx) =>
  api.delete(`/quotes/${quoteId}/zones/${zoneId}/photos/${idx}`).then((r) => r.data);
export const updateZonePhotoLabel = (quoteId, zoneId, idx, label) =>
  api.patch(`/quotes/${quoteId}/zones/${zoneId}/photos/${idx}`, { label }).then((r) => r.data);

// Dashboard
export const dashboardStats = () => api.get("/dashboard/stats").then((r) => r.data);

// v6 F15.3 — Schrijnwerk parts defaults
export const getJoineryPartsDefaults = () =>
  api.get("/joinery/parts-defaults").then((r) => r.data);

// v6 F15.3 Sprint B — Prijslijst Excel export/import
export const exportPriceCodesXlsxUrl = () =>
  `${API}/price-codes/export.xlsx`;
export const importTemplateXlsxUrl = () =>
  `${API}/price-codes/import-template.xlsx`;
export const previewPriceCodesImport = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/price-codes/import/preview", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const applyPriceCodesImport = (file, mode) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("mode", mode);
  return api.post("/price-codes/import/apply", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};

// v6 F-Invoicing F2 — Facturen
export const listInvoices = (params = {}) =>
  api.get("/invoices", { params }).then((r) => r.data);
export const getInvoice = (id) => api.get(`/invoices/${id}`).then((r) => r.data);
export const createFreeInvoice = (data) =>
  api.post("/invoices", data).then((r) => r.data);
export const createInvoiceFromQuote = (quoteId, data) =>
  api.post(`/invoices/from-quote/${quoteId}`, data).then((r) => r.data);
export const updateInvoiceDraft = (id, data) =>
  api.put(`/invoices/${id}`, data).then((r) => r.data);
export const finalizeInvoice = (id) =>
  api.post(`/invoices/${id}/finalize`).then((r) => r.data);
export const deleteInvoiceDraft = (id) =>
  api.delete(`/invoices/${id}`).then((r) => r.data);
// v14u Sprint 2A — Pro forma (doc_kind='pro_forma')
export const createProForma = (data) =>
  api.post("/invoices/pro-forma", data).then((r) => r.data);
// v14v Sprint 2B — Creditnota vanuit definitieve factuur (doc_kind='credit_note')
export const createCreditNote = (invoiceId, data) =>
  api.post(`/invoices/${invoiceId}/credit-note`, data).then((r) => r.data);
export const listInvoiceCreditNotes = (invoiceId) =>
  api.get(`/invoices/${invoiceId}/credit-notes`).then((r) => r.data);
export const getQuoteInvoiceable = (quoteId) =>
  api.get(`/quotes/${quoteId}/invoiceable`).then((r) => r.data);

// v6 F3.5b IA v2 — Country-aware BTW-regimes catalog.
export const getTaxRegimes = (country, locale) =>
  api.get(`/tax-regimes`, { params: { country, locale } }).then((r) => r.data);



// v14al — AutoMeasure™ Fase A (Vlaanderen). Wanneer 403 = toggle uit in Settings.
export const autoMeasureLookup = (address) =>
  api.post(`/automeasure/lookup`, { address }).then((r) => r.data);

// v14ao — Lichtgewicht adres-resolutie zonder footprint/DSM. Retourneert
// { resolved, address_resolved, x, y, match_confidence }.
export const autoMeasureResolveAddress = (address) =>
  api.post(`/automeasure/resolve-address`, { address }).then((r) => r.data);

// v14an — Persisteer de kardinale voorgevel-oriëntatie op het project.
export const updateProjectAutoMeasureFront = (id, cardinal) =>
  api.put(`/projects/${id}/automeasure`, { automeasure_front_orientation: cardinal }).then((r) => r.data);
